import Pusher from 'pusher-js';

import { domainMeta } from '@/utils/domain-meta';

/**
 * One Pusher socket per tab, opened on the first subscription and closed when
 * the last one is released. Pusher bills by concurrent connection.
 *
 * Private channels are signed by Hire's `/api_self_serve/v2/pusher/auth`. The
 * monolith runs on another origin, so the request carries the session cookie
 * through `fetch` with credentials, which pusher-js's own XHR transport omits.
 */

export type PusherConfig = { appKey: string; cluster: string };
export type PusherEventHandlers = Record<string, (data: unknown) => void>;

let client: Pusher | null = null;
let clientKey = '';
const channelRefCounts = new Map<string, number>();

const authorize = (
  { socketId, channelName }: { socketId: string; channelName: string },
  callback: (
    error: Error | null,
    data: { auth: string; channel_data?: string } | null,
  ) => void,
) => {
  fetch(`${domainMeta.monolithOrigin}/api_self_serve/v2/pusher/auth`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      socket_id: socketId,
      channel_name: channelName,
    }),
  })
    .then(async res => {
      if (!res.ok) throw new Error(`Pusher auth failed: ${res.status}`);
      callback(null, await res.json());
    })
    .catch((error: Error) => callback(error, null));
};

const getClient = ({ appKey, cluster }: PusherConfig): Pusher => {
  const key = `${appKey}:${cluster}`;
  if (client && clientKey !== key) {
    client.disconnect();
    client = null;
    channelRefCounts.clear();
  }
  if (!client) {
    client = new Pusher(appKey, {
      cluster,
      channelAuthorization: {
        transport: 'ajax',
        endpoint: '',
        customHandler: authorize,
      },
    });
    clientKey = key;
  }
  return client;
};

/**
 * Subscribes to a channel and binds the given handlers. Returns a release
 * function that unbinds only these handlers and drops the channel and the
 * socket when nobody else holds them.
 */
export const subscribeToPusherChannel = (
  config: PusherConfig,
  channelName: string,
  handlers: PusherEventHandlers,
): (() => void) => {
  if (!config.appKey || !channelName) return () => {};

  const pusher = getClient(config);
  const channel = pusher.subscribe(channelName);
  channelRefCounts.set(
    channelName,
    (channelRefCounts.get(channelName) ?? 0) + 1,
  );
  for (const [event, handler] of Object.entries(handlers)) {
    channel.bind(event, handler);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const [event, handler] of Object.entries(handlers)) {
      channel.unbind(event, handler);
    }
    const remaining = (channelRefCounts.get(channelName) ?? 1) - 1;
    if (remaining > 0) {
      channelRefCounts.set(channelName, remaining);
      return;
    }
    channelRefCounts.delete(channelName);
    if (pusher !== client) return;
    pusher.unsubscribe(channelName);
    if (channelRefCounts.size === 0) {
      pusher.disconnect();
      client = null;
    }
  };
};
